import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { foundationApi, serviceApiV3 } from '@/api/client';
import type { Envelope } from '@/api/envelope';

// ============================================
// Types  (ported from legacy @console v3 service)
// ============================================

export type ProjectStatus = 'active' | 'suspended' | 'archived';
export type ProjectTeamRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface ProjectMetadata {
  environment?: 'production' | 'staging' | 'development';
  region?: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface ProjectSettings {
  default_region?: string;
  notifications_enabled?: boolean;
  [key: string]: unknown;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  description: string;
  organization_id: string;
  owner_email: string;
  status: ProjectStatus;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
  teams_count?: number;
  resources_count?: number;
  created_at: string;
  updated_at: string;
  teams?: ProjectTeam[];
  /** Current user's role in this project. */
  user_role?: ProjectTeamRole;
}

export interface ProjectTeam {
  id: string;
  project_id: string;
  team_id: string;
  team_name?: string;
  team_description?: string;
  role: ProjectTeamRole;
  created_at: string;
  updated_at: string;
}

export interface ProjectResource {
  id: string;
  project_id: string;
  service_id: string;
  resource_type: string;
  resource_id: string;
  resource_name: string;
  resource_status: string;
  metadata?: Record<string, unknown>;
  assigned_by: string;
  assigned_at: string;
  synced_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: ProjectStatus;
  metadata?: ProjectMetadata;
  settings?: ProjectSettings;
}

export interface AddProjectTeamRequest {
  team_id: string;
  role: ProjectTeamRole;
}

export const PROJECT_TEAM_ROLES: {
  value: ProjectTeamRole;
  label: string;
  description: string;
}[] = [
  { value: 'owner', label: 'Owner', description: 'Full control including project deletion' },
  { value: 'admin', label: 'Admin', description: 'Manage teams and resources' },
  { value: 'member', label: 'Member', description: 'Create and manage own resources' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

// ============================================
// Response envelope helpers
// ============================================
// `/me/projects` returns a flatter, differently-named shape than `Project`.
interface UserProjectInfo {
  project_id: string;
  project_name: string;
  project_slug: string;
  organization_id: string;
  role: string;
  status: string;
  team_id?: string;
  teams_count?: number;
  created_at?: string;
}

function transformUserProject(p: UserProjectInfo): Project {
  return {
    id: p.project_id,
    name: p.project_name,
    slug: p.project_slug,
    description: '',
    organization_id: p.organization_id,
    owner_email: '',
    status: p.status as ProjectStatus,
    teams_count: p.teams_count ?? 0,
    created_at: p.created_at ?? '',
    updated_at: '',
    user_role: p.role as ProjectTeamRole,
  };
}

// ============================================
// Query keys
// ============================================

export const projectKeys = {
  all: ['v3', 'projects'] as const,
  myProjects: ['v3', 'me', 'projects'] as const,
  project: (id: string) => ['v3', 'projects', id] as const,
  teams: (id: string) => ['v3', 'projects', id, 'teams'] as const,
  resources: (id: string) => ['v3', 'projects', id, 'resources'] as const,
  candidates: (id: string, params: DiscoverCandidatesParams) =>
    ['v3', 'projects', id, 'candidates', params] as const,
};

// ============================================
// Query hooks
// ============================================

export function useMyProjects(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: projectKeys.myProjects,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ projects: UserProjectInfo[] }>>(
        '/me/projects',
      );
      return (res.data?.data?.projects ?? []).map(transformUserProject);
    },
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.project(id ?? ''),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<Project | null> => {
      try {
        const res = await foundationApi.get<Envelope<Project>>(`/projects/${id}`);
        return res.data?.data ?? null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // No access / not found are handled gracefully in the UI.
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

export function useProjectTeams(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.teams(projectId ?? ''),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ teams: ProjectTeam[] }>>(
        `/projects/${projectId}/teams`,
      );
      return res.data?.data?.teams ?? [];
    },
  });
}

export function useProjectResources(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.resources(projectId ?? ''),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ resources: ProjectResource[] }>>(
        `/projects/${projectId}/resources`,
      );
      return res.data?.data?.resources ?? [];
    },
  });
}

// ============================================
// Mutation hooks
// ============================================

/** Invalidate the my-projects list + a single project's caches. */
function useProjectInvalidation() {
  const qc = useQueryClient();
  return {
    list: () => qc.invalidateQueries({ queryKey: projectKeys.myProjects }),
    project: (id: string) => qc.invalidateQueries({ queryKey: projectKeys.project(id) }),
    teams: (id: string) => qc.invalidateQueries({ queryKey: projectKeys.teams(id) }),
  };
}

export function useCreateProject(
  options?: UseMutationOptions<Project, unknown, CreateProjectRequest>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (body: CreateProjectRequest) => {
      const res = await foundationApi.post<Envelope<Project>>('/projects', body);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateProject(
  id: string,
  options?: UseMutationOptions<Project, unknown, UpdateProjectRequest>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (body: UpdateProjectRequest) => {
      const res = await foundationApi.put<Envelope<Project>>(`/projects/${id}`, body);
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      inv.project(id);
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteProject(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      await foundationApi.delete(`/projects/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

/** Flip a project's `status` (archive ↔ restore). `mutate` takes the project id. */
function useProjectStatusMutation(
  status: ProjectStatus,
  options?: UseMutationOptions<Project, unknown, string>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await foundationApi.put<Envelope<Project>>(`/projects/${id}`, { status });
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      const id = args[1];
      inv.list();
      inv.project(id);
      options?.onSuccess?.(...args);
    },
  });
}

/** Archive (soft-delete) a project. `mutate(projectId)`. */
export function useArchiveProject(
  options?: UseMutationOptions<Project, unknown, string>,
) {
  return useProjectStatusMutation('archived', options);
}

/** Restore an archived project. `mutate(projectId)`. */
export function useRestoreProject(
  options?: UseMutationOptions<Project, unknown, string>,
) {
  return useProjectStatusMutation('active', options);
}

export function useAddProjectTeam(
  projectId: string,
  options?: UseMutationOptions<ProjectTeam, unknown, AddProjectTeamRequest>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (body: AddProjectTeamRequest) => {
      const res = await foundationApi.post<Envelope<ProjectTeam>>(
        `/projects/${projectId}/teams`,
        body,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.teams(projectId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateProjectTeam(
  projectId: string,
  options?: UseMutationOptions<
    ProjectTeam,
    unknown,
    { teamId: string; role: ProjectTeamRole }
  >,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async ({ teamId, role }) => {
      const res = await foundationApi.put<Envelope<ProjectTeam>>(
        `/projects/${projectId}/teams/${teamId}`,
        { role },
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      inv.teams(projectId);
      options?.onSuccess?.(...args);
    },
  });
}

export function useRemoveProjectTeam(
  projectId: string,
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useProjectInvalidation();
  return useMutation({
    mutationFn: async (teamId: string) => {
      await foundationApi.delete(`/projects/${projectId}/teams/${teamId}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.teams(projectId);
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Auto Discovery — candidates + auto-assign
// These two stay on @mgmt (serviceApiV3): they query the instances/domains tables,
// which margarita-foundation does not own (foundation#64). Both services share one
// DB for now, so they keep working against the same project_resources rows.
// ============================================

export type CandidateResourceType = 'domain' | 'instance';
export type CandidateAssignment = 'unassigned' | 'this_project' | 'other_project';

export interface CandidateResource {
  service_id: string;
  resource_type: CandidateResourceType;
  resource_id: string;
  resource_name: string;
  resource_status: string;
  tags: string[];
  created_at: string;
  assignment: CandidateAssignment;
  assigned_project_id?: string;
  assigned_project_name?: string;
}

export interface CandidateCounts {
  unassigned: number;
  this_project: number;
  other_project: number;
}

export interface CandidatesResponse {
  items: CandidateResource[];
  total: number;
  counts: CandidateCounts;
  page: number;
  per_page: number;
}

/** Query params for the candidates endpoint. CSV fields are joined before send. */
export interface DiscoverCandidatesParams {
  /** One or both of 'domain' | 'instance'. Omitted → backend defaults to both. */
  resource_types?: CandidateResourceType[];
  status?: string;
  search?: string;
  tags?: string[];
  assignment?: 'all' | 'unassigned';
  created_from?: string;
  created_to?: string;
  page?: number;
  per_page?: number;
}

/** Server-side filter block shared by candidates + auto-assign. */
export interface AutoAssignFilters {
  resource_types?: string[];
  status?: string;
  search?: string;
  tags?: string[];
  created_from?: string;
  created_to?: string;
}

export interface AutoAssignRequest {
  filters: AutoAssignFilters;
  include_ids?: string[];
  exclude_ids?: string[];
  dry_run: boolean;
}

export interface AutoAssignConflict {
  resource_id: string;
  resource_type: string;
  project_id: string;
  project_name: string;
}

export interface AutoAssignResult {
  matched: number;
  assigned: number;
  skipped_already_in_project: number;
  conflicts: AutoAssignConflict[];
  dry_run: boolean;
}

/** Build the axios query object from typed params (CSV-joining list fields). */
function candidatesQuery(params: DiscoverCandidatesParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (params.resource_types?.length) q.resource_type = params.resource_types.join(',');
  if (params.status) q.status = params.status;
  if (params.search) q.search = params.search;
  if (params.tags?.length) q.tags = params.tags.join(',');
  if (params.assignment) q.assignment = params.assignment;
  if (params.created_from) q.created_from = params.created_from;
  if (params.created_to) q.created_to = params.created_to;
  if (params.page) q.page = params.page;
  if (params.per_page) q.per_page = params.per_page;
  return q;
}

export interface BulkResourceItem {
  service_id: string;
  resource_type: string;
  resource_id: string;
  resource_name?: string;
}

/** Bulk assign/unassign resources to a project (POST /projects/:id/resources/bulk). */
export function useBulkAssignProjectResources(
  options?: UseMutationOptions<
    unknown,
    unknown,
    { projectId: string; action: 'assign' | 'unassign'; resources: BulkResourceItem[] }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, action, resources }) => {
      const res = await foundationApi.post(`/projects/${projectId}/resources/bulk`, { action, resources });
      return res.data?.data ?? null;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: projectKeys.resources(args[1].projectId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Auto Discovery hooks
// ============================================

type DiscoverQueryOptions = Omit<
  UseQueryOptions<CandidatesResponse>,
  'queryKey' | 'queryFn'
>;

/**
 * Discover assignable resources (CDN domains + compute instances) for a project,
 * filtered by conditions. Paginated; previous page data is kept while the next
 * page loads to avoid layout flashes.
 */
export function useDiscoverCandidates(
  projectId: string | undefined,
  params: DiscoverCandidatesParams,
  options?: DiscoverQueryOptions,
) {
  return useQuery<CandidatesResponse>({
    queryKey: projectKeys.candidates(projectId ?? '', params),
    enabled: !!projectId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get<Envelope<CandidatesResponse>>(
        `/projects/${projectId}/resources/candidates`,
        { params: candidatesQuery(params) },
      );
      return (
        res.data?.data ?? {
          items: [],
          total: 0,
          counts: { unassigned: 0, this_project: 0, other_project: 0 },
          page: params.page ?? 1,
          per_page: params.per_page ?? 50,
        }
      );
    },
  });
}

/**
 * Auto-assign matching resources to a project. Supports `dry_run` (preview the
 * matched/assigned/conflict counts without committing) and real commits. On a
 * committed (non-dry) success, the project's resource + candidate caches are
 * invalidated.
 */
export function useAutoAssign(
  projectId: string,
  options?: UseMutationOptions<AutoAssignResult, unknown, AutoAssignRequest>,
) {
  const qc = useQueryClient();
  return useMutation<AutoAssignResult, unknown, AutoAssignRequest>({
    mutationFn: async (body: AutoAssignRequest) => {
      const res = await serviceApiV3.post<Envelope<AutoAssignResult>>(
        `/projects/${projectId}/resources/auto-assign`,
        body,
      );
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      const [, request] = args;
      if (!request.dry_run) {
        qc.invalidateQueries({ queryKey: projectKeys.resources(projectId) });
        qc.invalidateQueries({ queryKey: ['v3', 'projects', projectId, 'candidates'] });
      }
      options?.onSuccess?.(...args);
    },
  });
}
